import { KWC2026_TRICKS } from './constants';
import { isLocalBackend, phpApiUrl, phpAuthHeaders } from './apiEnv';
import type {
  Kwc2026PointEntryDetail,
  Kwc2026PointLeaderboardEntry,
  Kwc2026PointProfile,
  Kwc2026PointScoreBreakdownEntry,
  Kwc2026PointScoreSummary,
  Kwc2026PointSubmission,
  Kwc2026PointTrickDetail,
  Kwc2026PointVideo,
  TournamentTrick
} from './types';

export const KWC2026_POINT_MODE_NAME = 'KWC模拟决赛';
export const KWC2026_POINT_MAX_TRICK_COUNT = 30;
// 3 分 20 秒：视频本身是 3 分钟内打完所选招式组的录像，多出的时间是给入场/收拾留的余量。
export const KWC2026_POINT_MAX_VIDEO_DURATION_SECONDS = 200;
export const KWC2026_POINT_MAX_VIDEO_DURATION_MS = KWC2026_POINT_MAX_VIDEO_DURATION_SECONDS * 1000;
// 手机读元数据会有零点几秒误差，校验时留一点容差，避免 200.0s 的视频被误判。
export const KWC2026_POINT_MAX_VIDEO_DURATION_TOLERANCE_SECONDS = 1;
export const KWC2026_POINT_MAX_VIDEO_SIZE = 300 * 1024 * 1024;
export const KWC2026_POINT_CHUNK_SIZE = 2 * 1024 * 1024;
// 本地开发/预览时补一批虚拟选手，方便测试排行榜滚动与长列表布局。
export const KWC2026_POINT_LOCAL_MOCK_PREFIX = 'local-mock-';
export const KWC2026_POINT_LOCAL_MOCK_COUNT = 50;

const UPLOAD_TIMEOUT_MS = 30 * 60 * 1000;

const KWC2026_POINT_TRICK_ID_PATTERN = /^kwc2026-(\d{1,2})-(\d{1,2})$/;
const KWC2026_POINT_ALLOWED_MIME_TYPES = new Set([
  'video/mp4',
  'video/webm',
  'video/quicktime',
  'video/ogg',
  'video/x-matroska'
]);
const KWC2026_POINT_VIDEO_EXTENSION_MIME_MAP: Record<string, string> = {
  mp4: 'video/mp4',
  m4v: 'video/mp4',
  mov: 'video/quicktime',
  webm: 'video/webm',
  ogv: 'video/ogg',
  ogg: 'video/ogg',
  mkv: 'video/x-matroska'
};

const trickMap = new Map(KWC2026_TRICKS.map((trick) => [trick.id, trick]));

// 统一走后端请求封装：本地开发用 Node 的 /api 路径；网页/原生 App 走 PHP 接口
// （原生 App 需要线上绝对地址 + X-Kendama-Auth，跨域拿不到 Cookie）。
const apiRequest = (localPath: string, phpAction: string, init: RequestInit = {}): Promise<Response> =>
  fetch(
    isLocalBackend() ? localPath : phpApiUrl(phpAction),
    { ...init, headers: phpAuthHeaders((init.headers as Record<string, string>) || undefined) }
  );

const parseJsonResponse = async (response: Response) => {
  const raw = await response.text();
  if (!raw) return {};
  try {
    return JSON.parse(raw);
  } catch {
    throw new Error(`Non-JSON response (${response.status})`);
  }
};

const xhrUpload = (
  url: string,
  method: string,
  headers: Record<string, string> | null,
  body: Document | XMLHttpRequestBodyInit | null,
  onProgress: ((pct: number) => void) | undefined,
  timeoutMs: number
): Promise<{ ok: boolean; status: number; text: () => Promise<string> }> =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url);
    xhr.timeout = timeoutMs;
    xhr.withCredentials = true;

    if (headers) {
      Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    }

    if (onProgress) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          onProgress(Math.min(99, Math.round((event.loaded / event.total) * 100)));
        }
      };
    }

    xhr.onload = () => {
      resolve({
        ok: xhr.status >= 200 && xhr.status < 300,
        status: xhr.status,
        text: () => Promise.resolve(xhr.responseText)
      });
    };

    xhr.onerror = () => reject(new Error('网络错误，请检查网络连接后重试。'));
    xhr.ontimeout = () => reject(new Error('上传超时，请检查网络后重试。'));

    xhr.send(body);
  });

const normalizeKwc2026PointVideoUrl = (rawUrl: string) => {
  const trimmedUrl = String(rawUrl || '').trim();
  if (!trimmedUrl || typeof window === 'undefined') {
    return trimmedUrl;
  }

  if (trimmedUrl.startsWith('blob:') || trimmedUrl.startsWith('data:')) {
    return trimmedUrl;
  }

  try {
    const parsedUrl = new URL(trimmedUrl, window.location.href);
    const isUploadPath = parsedUrl.pathname.includes('/uploads/')
      || parsedUrl.pathname.includes('/kwc2026point/');

    // 混合内容会被浏览器拦下来：HTTPS 页面里把 http 的资源地址换回同源地址再加载。
    if (window.location.protocol === 'https:' && parsedUrl.protocol === 'http:' && isUploadPath) {
      return `${window.location.origin}${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
    }

    return parsedUrl.toString();
  } catch {
    return trimmedUrl;
  }
};

export const getKwc2026PointTrickLevel = (trickId: string): number | null => {
  const knownTrick = trickMap.get(trickId);
  if (knownTrick) return knownTrick.score;

  const matched = KWC2026_POINT_TRICK_ID_PATTERN.exec(String(trickId || '').trim());
  if (!matched) return null;
  const level = Number(matched[1]);
  const index = Number(matched[2]);
  if (!Number.isInteger(level) || level < 1 || level > 15) return null;
  if (!Number.isInteger(index) || index < 1 || index > 10) return null;
  return level;
};

export const getKwc2026PointTrick = (trickId: string): TournamentTrick | undefined =>
  trickMap.get(trickId);

export const getKwc2026PointTrickName = (trickId: string): string => {
  const knownTrick = trickMap.get(trickId);
  if (knownTrick) return knownTrick.name;

  const matched = KWC2026_POINT_TRICK_ID_PATTERN.exec(String(trickId || '').trim());
  if (matched) {
    return `KWC2026 ${matched[1]}-${matched[2]}`;
  }

  return trickId;
};

export const getKwc2026PointBonusForScore = (score: number) => {
  if (score === 13) return 30;
  if (score === 14) return 100;
  if (score === 15) return 225;
  return 0;
};

// 积分赛 KWC2026 决赛规则：
// 乘算分 = Σ 等级²，奖励分 = 13级+30 / 14级+100 / 15级+225，
// 视频代表整套招式全部成功，所以再加上「全成追加」= Σ 等级。
export const computeKwc2026PointScore = (trickIds: string[]): Kwc2026PointScoreSummary => {
  const summary = new Map<number, number>();
  let squaredScore = 0;
  let bonusScore = 0;
  let allSuccessBonusScore = 0;
  let trickCount = 0;

  (Array.isArray(trickIds) ? trickIds : []).forEach((trickId) => {
    const level = getKwc2026PointTrickLevel(trickId);
    if (level === null) return;
    trickCount += 1;
    squaredScore += Math.pow(level, 2);
    bonusScore += getKwc2026PointBonusForScore(level);
    allSuccessBonusScore += level;
    summary.set(level, (summary.get(level) || 0) + 1);
  });

  const breakdown: Kwc2026PointScoreBreakdownEntry[] = Array.from(summary.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([score, count]) => ({
      score,
      count,
      squaredSubtotal: Math.pow(score, 2) * count,
      bonusSubtotal: count * getKwc2026PointBonusForScore(score)
    }));

  return {
    totalScore: squaredScore + bonusScore + allSuccessBonusScore,
    squaredScore,
    bonusScore,
    allSuccessBonusScore,
    trickCount,
    breakdown
  };
};

export const buildKwc2026PointTrickDetails = (trickIds: string[]): Kwc2026PointTrickDetail[] =>
  (Array.isArray(trickIds) ? trickIds : [])
    .map((trickId) => {
      const level = getKwc2026PointTrickLevel(trickId);
      if (level === null) return null;
      return { id: trickId, level, score: level };
    })
    .filter((item): item is Kwc2026PointTrickDetail => !!item);

const normalizeVideo = (raw: any): Kwc2026PointVideo | null => {
  const url = normalizeKwc2026PointVideoUrl(String(raw?.url || ''));
  const uploadedAt = Number(raw?.uploadedAt);
  const rawStatus = typeof raw?.status === 'string' ? raw.status.trim().toLowerCase() : '';
  const status: 'processing' | 'ready' | 'failed' =
    rawStatus === 'processing' || rawStatus === 'failed' ? rawStatus : 'ready';
  // 只有 ready 才必须有可播放地址；转码中 / 转码失败可能还没有 url，不能丢。
  if ((!url && status === 'ready') || !Number.isFinite(uploadedAt)) return null;

  return {
    url,
    objectKey: typeof raw?.objectKey === 'string' ? raw.objectKey : undefined,
    sourceObjectKey: typeof raw?.sourceObjectKey === 'string' ? raw.sourceObjectKey : undefined,
    fileName: typeof raw?.fileName === 'string' ? raw.fileName : undefined,
    mimeType: typeof raw?.mimeType === 'string' ? raw.mimeType : undefined,
    size: Number.isFinite(Number(raw?.size)) ? Number(raw.size) : undefined,
    durationMs: Number.isFinite(Number(raw?.durationMs)) ? Number(raw.durationMs) : undefined,
    uploadedAt,
    status,
    processingProgress: Number.isFinite(Number(raw?.processingProgress))
      ? Math.max(0, Math.min(100, Number(raw.processingProgress)))
      : undefined,
    processingPhase: typeof raw?.processingPhase === 'string' ? raw.processingPhase : undefined,
    processingMessage: typeof raw?.processingMessage === 'string' ? raw.processingMessage : undefined,
    taskId: typeof raw?.taskId === 'string' ? raw.taskId : undefined,
    sourceUrl: typeof raw?.sourceUrl === 'string' ? normalizeKwc2026PointVideoUrl(raw.sourceUrl) : undefined
  };
};

const normalizeScore = (raw: any, fallbackTrickIds: string[] = []): Kwc2026PointScoreSummary => {
  const breakdown = Array.isArray(raw?.breakdown)
    ? raw.breakdown
        .map((item: any) => ({
          score: Number(item?.score),
          count: Number(item?.count),
          squaredSubtotal: Math.pow(Number(item?.score), 2) * Number(item?.count),
          bonusSubtotal: Number(item?.bonusSubtotal) || Number(item?.count) * getKwc2026PointBonusForScore(Number(item?.score))
        }))
        .filter((item: Kwc2026PointScoreBreakdownEntry) => Number.isFinite(item.score) && Number.isFinite(item.count) && item.count > 0)
    : [];

  const computed = computeKwc2026PointScore(fallbackTrickIds);
  const squaredScore = Number.isFinite(Number(raw?.squaredScore)) ? Number(raw.squaredScore) : computed.squaredScore;
  const bonusScore = Number.isFinite(Number(raw?.bonusScore)) ? Number(raw.bonusScore) : computed.bonusScore;
  const allSuccessBonusScore = Number.isFinite(Number(raw?.allSuccessBonusScore))
    ? Number(raw.allSuccessBonusScore)
    : computed.allSuccessBonusScore;
  const totalScore = Number.isFinite(Number(raw?.totalScore))
    ? Number(raw.totalScore)
    : squaredScore + bonusScore + allSuccessBonusScore;

  return {
    totalScore,
    squaredScore,
    bonusScore,
    allSuccessBonusScore,
    trickCount: Number.isFinite(Number(raw?.trickCount)) ? Number(raw.trickCount) : computed.trickCount,
    breakdown: breakdown.length > 0 ? breakdown : computed.breakdown
  };
};

const normalizeTrickDetails = (raw: any, fallbackTrickIds: string[] = []): Kwc2026PointTrickDetail[] => {
  if (Array.isArray(raw) && raw.length > 0) {
    const normalized = raw
      .map((item: any) => {
        const id = String(item?.id || '').trim();
        const level = getKwc2026PointTrickLevel(id);
        if (!id || level === null) return null;
        return { id, level, score: level };
      })
      .filter((item): item is Kwc2026PointTrickDetail => !!item);
    if (normalized.length > 0) return normalized;
  }
  return buildKwc2026PointTrickDetails(fallbackTrickIds);
};

const normalizeSubmission = (raw: any): Kwc2026PointSubmission | null => {
  const trickIds = Array.isArray(raw?.trickIds)
    ? raw.trickIds.map((item: unknown) => String(item || '').trim()).filter((item: string) => !!item)
    : [];
  const tricks = normalizeTrickDetails(raw?.tricks, trickIds);
  const video = normalizeVideo(raw?.video);
  const submittedAt = Number(raw?.submittedAt);
  if (tricks.length === 0 || !video || !Number.isFinite(submittedAt)) return null;

  return {
    trickIds: trickIds.length > 0 ? trickIds : tricks.map((trick) => trick.id),
    tricks,
    score: normalizeScore(raw?.score, tricks.map((trick) => trick.id)),
    video,
    submittedAt
  };
};

export const normalizeKwc2026PointProfile = (raw: unknown): Kwc2026PointProfile => {
  const source = (raw && typeof raw === 'object' ? raw : {}) as Partial<Kwc2026PointProfile>;
  const selectedTricks = Array.isArray(source.selectedTricks)
    ? source.selectedTricks
        .map((item) => String(item || '').trim())
        .filter((item) => getKwc2026PointTrickLevel(item) !== null)
        .slice(0, KWC2026_POINT_MAX_TRICK_COUNT)
    : [];
  const savedTricksAt = Number.isFinite(Number(source.savedTricksAt)) ? Number(source.savedTricksAt) : null;

  return {
    modeName: typeof source.modeName === 'string' && source.modeName.trim() ? source.modeName : KWC2026_POINT_MODE_NAME,
    selectedTricks,
    savedTricksAt,
    video: normalizeVideo(source.video),
    submission: normalizeSubmission(source.submission)
  };
};

export const createEmptyKwc2026PointProfile = (): Kwc2026PointProfile => ({
  modeName: KWC2026_POINT_MODE_NAME,
  selectedTricks: [],
  savedTricksAt: null,
  video: null,
  submission: null
});

// 转码机（与 CKO2026 线上赛同一套）处理完之前没有可播放地址，这里统一判断状态。
export const isKwc2026PointVideoReady = (video?: Kwc2026PointVideo | null) =>
  !!video && (video.status ?? 'ready') === 'ready' && !!video.url;

export const isKwc2026PointVideoProcessing = (video?: Kwc2026PointVideo | null) =>
  !!video && video.status === 'processing';

export const isKwc2026PointVideoFailed = (video?: Kwc2026PointVideo | null) =>
  !!video && video.status === 'failed';

export const isKwc2026PointLocalMockUserId = (userId: string) =>
  String(userId || '').startsWith(KWC2026_POINT_LOCAL_MOCK_PREFIX);

export const isKwc2026PointLocalMockEnabled = () => isLocalBackend();

const KWC2026_POINT_LOCAL_MOCK_NAMES = [
  '剑玉小王子',
  '皿上舞者',
  '灯台爱好者',
  '跳剑日常',
  '止剑练习生',
  '宇宙游泳选手',
  '中皿极意',
  '竹马不落地',
  '小丑止剑',
  '离转地球',
  '月面着陆',
  '大皿快手',
  '玉中杀爱好者',
  '逆月面挑战者',
  '左撇子剑玉',
  '周末练剑玉',
  '廿日市预备军',
  '广岛远征队',
  'KWC冲刺班',
  '三分钟挑战者'
];

// 固定的招式池：让每个虚拟选手的招式组和总分自洽（详情页点进去和列表一致）。
const KWC2026_POINT_LOCAL_MOCK_POOL = KWC2026_TRICKS.filter((trick) => trick.score >= 3);

const buildKwc2026PointLocalMockTrickIds = (index: number, count: number) => {
  const ids: string[] = [];
  const poolSize = KWC2026_POINT_LOCAL_MOCK_POOL.length;
  if (poolSize === 0) return ids;

  let cursor = (index * 17 + 5) % poolSize;
  for (let picked = 0; picked < count && picked < poolSize; picked += 1) {
    ids.push(KWC2026_POINT_LOCAL_MOCK_POOL[cursor].id);
    cursor = (cursor + 11) % poolSize;
    if (ids.length < count && KWC2026_POINT_LOCAL_MOCK_POOL[cursor] && ids.includes(KWC2026_POINT_LOCAL_MOCK_POOL[cursor].id)) {
      cursor = (cursor + 1) % poolSize;
    }
  }
  return Array.from(new Set(ids));
};

export interface Kwc2026PointLocalMockEntry extends Kwc2026PointLeaderboardEntry {
  isLocalMock: true;
}

// 生成 50 个固定的虚拟选手（分数、招数、时间都可复现），只用于本地测试。
export const buildKwc2026PointLocalMockLeaderboard = (): Kwc2026PointLeaderboardEntry[] => {
  const baseTime = Date.now();
  return Array.from({ length: KWC2026_POINT_LOCAL_MOCK_COUNT }, (_, index) => {
    const trickCount = 3 + ((index * 5) % 10);
    const trickIds = buildKwc2026PointLocalMockTrickIds(index, trickCount);
    const score = computeKwc2026PointScore(trickIds);
    const name = KWC2026_POINT_LOCAL_MOCK_NAMES[index % KWC2026_POINT_LOCAL_MOCK_NAMES.length];
    return {
      userId: `${KWC2026_POINT_LOCAL_MOCK_PREFIX}${index + 1}`,
      displayName: `${name}${String(index + 1).padStart(2, '0')}`,
      avatarUrl: undefined,
      totalScore: score.totalScore,
      trickCount: trickIds.length,
      submittedAt: baseTime - (index + 1) * 37 * 60 * 1000
    };
  });
};

export const buildKwc2026PointLocalMockEntry = (userId: string): Kwc2026PointEntryDetail | null => {
  const entry = buildKwc2026PointLocalMockLeaderboard().find((item) => item.userId === userId);
  if (!entry) return null;
  const index = Number(String(userId).replace(KWC2026_POINT_LOCAL_MOCK_PREFIX, '')) - 1;
  const trickIds = buildKwc2026PointLocalMockTrickIds(Number.isFinite(index) && index >= 0 ? index : 0, entry.trickCount);
  return {
    ...entry,
    video: null,
    tricks: buildKwc2026PointTrickDetails(trickIds),
    score: computeKwc2026PointScore(trickIds)
  };
};

export const fetchKwc2026PointProfile = async (): Promise<Kwc2026PointProfile> => {
  const response = await apiRequest('/api/kwc2026-point/profile', 'kwc2026_point_profile');
  const data = await parseJsonResponse(response);
  if (!response.ok || data?.success === false) {
    throw new Error(data?.error || '活动数据获取失败');
  }
  return normalizeKwc2026PointProfile(data?.profile);
};

export const saveKwc2026PointTricks = async (trickIds: string[]): Promise<Kwc2026PointProfile> => {
  const response = await apiRequest(
    '/api/kwc2026-point/tricks',
    'kwc2026_point_save_tricks',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ trickIds })
    }
  );
  const data = await parseJsonResponse(response);
  if (!response.ok || data?.success === false) {
    throw new Error(data?.error || '招式保存失败');
  }
  return normalizeKwc2026PointProfile(data?.profile);
};

const uploadKwc2026PointVideoLocally = async (
  file: File,
  durationMs: number,
  onProgress?: (pct: number) => void
): Promise<Kwc2026PointProfile> => {
  const mimeType = inferKwc2026PointVideoMimeType(file) || 'video/mp4';
  const uploadId = `kwc2026point-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  const totalChunks = Math.max(1, Math.ceil(file.size / KWC2026_POINT_CHUNK_SIZE));

  for (let index = 0; index < totalChunks; index += 1) {
    const start = index * KWC2026_POINT_CHUNK_SIZE;
    const chunk = file.slice(start, Math.min(file.size, start + KWC2026_POINT_CHUNK_SIZE));
    const response = await apiRequest(
      '/api/kwc2026-point/video/chunk',
      'kwc2026_point_video_chunk',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/octet-stream',
          'x-upload-id': uploadId,
          'x-chunk-index': String(index),
          'x-total-chunks': String(totalChunks),
          'x-total-size': String(file.size),
          'x-file-name': encodeURIComponent(file.name || 'submission'),
          'x-mime-type': mimeType,
          'x-duration-ms': String(Math.round(durationMs))
        },
        body: chunk
      }
    );
    const data = await parseJsonResponse(response);
    if (!response.ok || data?.success === false) {
      throw new Error(data?.error || '视频上传失败');
    }
    if (onProgress) {
      onProgress(Math.min(99, Math.round(((index + 1) / totalChunks) * 100)));
    }
  }

  const completeResponse = await apiRequest(
    '/api/kwc2026-point/video/chunk-complete',
    'kwc2026_point_video_chunk_complete',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        uploadId,
        totalChunks,
        totalSize: file.size,
        fileName: file.name || 'submission',
        mimeType,
        durationMs: Math.round(durationMs)
      })
    }
  );
  const completeData = await parseJsonResponse(completeResponse);
  if (!completeResponse.ok || completeData?.success === false) {
    throw new Error(completeData?.error || '视频保存失败');
  }
  if (onProgress) {
    onProgress(100);
  }
  return normalizeKwc2026PointProfile(completeData?.profile);
};

const uploadKwc2026PointVideoToOss = async (
  file: File,
  durationMs: number,
  onProgress?: (pct: number) => void
): Promise<Kwc2026PointProfile> => {
  const mimeType = inferKwc2026PointVideoMimeType(file) || 'video/mp4';
  const initResponse = await apiRequest(
    '/api/kwc2026-point/video/init',
    'kwc2026_point_video_init',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        fileName: file.name || 'submission',
        mimeType,
        fileSize: file.size,
        durationMs: Math.round(durationMs)
      })
    }
  );
  const initData = await parseJsonResponse(initResponse);
  if (!initResponse.ok || initData?.success === false || typeof initData?.putUrl !== 'string' || typeof initData?.uploadId !== 'string') {
    throw new Error(initData?.error || '初始化视频上传失败');
  }

  const putHeaders =
    initData?.headers && typeof initData.headers === 'object'
      ? Object.fromEntries(
          Object.entries(initData.headers as Record<string, unknown>)
            .filter((entry): entry is [string, string] => typeof entry[0] === 'string' && typeof entry[1] === 'string')
        )
      : { 'Content-Type': mimeType };

  const putResponse = await xhrUpload(
    initData.putUrl,
    'PUT',
    putHeaders,
    file,
    onProgress ? (pct) => onProgress(Math.min(95, Math.max(1, Math.round(pct * 0.95)))) : undefined,
    UPLOAD_TIMEOUT_MS
  );
  if (!putResponse.ok) {
    throw new Error('视频直传失败，请稍后重试。');
  }

  const completeResponse = await apiRequest(
    '/api/kwc2026-point/video/complete',
    'kwc2026_point_video_complete',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ uploadId: initData.uploadId })
    }
  );
  const completeData = await parseJsonResponse(completeResponse);
  if (!completeResponse.ok || completeData?.success === false) {
    throw new Error(completeData?.error || '视频上传确认失败');
  }
  if (onProgress) {
    onProgress(100);
  }
  return normalizeKwc2026PointProfile(completeData?.profile);
};

export const uploadKwc2026PointVideo = async (
  file: File,
  durationMs: number,
  onProgress?: (pct: number) => void
): Promise<Kwc2026PointProfile> =>
  isLocalBackend()
    ? uploadKwc2026PointVideoLocally(file, durationMs, onProgress)
    : uploadKwc2026PointVideoToOss(file, durationMs, onProgress);

export const submitKwc2026PointEntry = async (): Promise<Kwc2026PointProfile> => {
  const response = await apiRequest(
    '/api/kwc2026-point/submit',
    'kwc2026_point_submit',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    }
  );
  const data = await parseJsonResponse(response);
  if (!response.ok || data?.success === false) {
    throw new Error(data?.error || '投稿失败');
  }
  return normalizeKwc2026PointProfile(data?.profile);
};

const normalizeLeaderboardEntry = (raw: any): Kwc2026PointLeaderboardEntry | null => {
  const userId = String(raw?.userId || '').trim();
  if (!userId) return null;
  return {
    userId,
    displayName: String(raw?.displayName || 'Unknown Player'),
    avatarUrl: typeof raw?.avatarUrl === 'string' && raw.avatarUrl ? raw.avatarUrl : undefined,
    totalScore: Number(raw?.totalScore) || 0,
    trickCount: Number(raw?.trickCount) || 0,
    submittedAt: Number.isFinite(Number(raw?.submittedAt)) ? Number(raw.submittedAt) : 0
  };
};

export const fetchKwc2026PointLeaderboard = async (): Promise<Kwc2026PointLeaderboardEntry[]> => {
  const response = await apiRequest('/api/kwc2026-point/leaderboard', 'kwc2026_point_leaderboard');
  const data = await parseJsonResponse(response);
  if (!response.ok || data?.success === false) {
    throw new Error(data?.error || '排行榜获取失败');
  }

  const entries: Kwc2026PointLeaderboardEntry[] = Array.isArray(data?.leaderboard)
    ? data.leaderboard
        .map(normalizeLeaderboardEntry)
        .filter((entry: Kwc2026PointLeaderboardEntry | null): entry is Kwc2026PointLeaderboardEntry => !!entry)
    : [];

  // 本地开发时额外补 50 个虚拟选手，方便测试排行榜长列表的滚动与布局。
  const merged = isKwc2026PointLocalMockEnabled()
    ? [...entries, ...buildKwc2026PointLocalMockLeaderboard()]
    : entries;

  return merged.sort((a, b) => b.totalScore - a.totalScore || a.submittedAt - b.submittedAt);
};

export const fetchKwc2026PointEntry = async (userId: string): Promise<Kwc2026PointEntryDetail> => {
  // 虚拟选手的详情在本地直接构造，不去打后端。
  if (isKwc2026PointLocalMockUserId(userId)) {
    const mockEntry = buildKwc2026PointLocalMockEntry(userId);
    if (!mockEntry) throw new Error('本地测试选手不存在');
    return mockEntry;
  }

  const response = await apiRequest(
    '/api/kwc2026-point/entry',
    'kwc2026_point_entry',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ targetUserId: userId })
    }
  );
  const data = await parseJsonResponse(response);
  if (!response.ok || data?.success === false || !data?.entry) {
    throw new Error(data?.error || '选手详情获取失败');
  }

  const entry = data.entry;
  const trickIds = Array.isArray(entry?.trickIds)
    ? entry.trickIds.map((item: unknown) => String(item || '').trim()).filter((item: string) => !!item)
    : [];
  const tricks = normalizeTrickDetails(entry?.tricks, trickIds);
  const fallbackTrickIds = tricks.map((trick) => trick.id);
  const normalizedBase = normalizeLeaderboardEntry({
    ...entry,
    totalScore: Number(entry?.score?.totalScore ?? entry?.totalScore) || 0,
    trickCount: tricks.length
  });

  if (!normalizedBase) {
    throw new Error('选手详情数据异常');
  }

  return {
    ...normalizedBase,
    video: normalizeVideo(entry?.video),
    tricks,
    score: normalizeScore(entry?.score, fallbackTrickIds)
  };
};

export const inferKwc2026PointVideoMimeType = (file: File) => {
  const normalizedType = String(file.type || '').split(';')[0].trim().toLowerCase();
  if (KWC2026_POINT_ALLOWED_MIME_TYPES.has(normalizedType)) {
    return normalizedType;
  }
  if (normalizedType.startsWith('video/')) {
    return normalizedType;
  }

  const fileName = String(file.name || '').trim().toLowerCase();
  const ext = fileName.includes('.') ? fileName.split('.').pop() || '' : '';
  return KWC2026_POINT_VIDEO_EXTENSION_MIME_MAP[ext] || '';
};

export const isKwc2026PointVideoFile = (file: File) =>
  String(file.type || '').startsWith('video/')
  || /\.(mp4|m4v|mov|webm|ogg|ogv|mkv)$/i.test(String(file.name || ''));

// 读取视频时长（秒）。用 object URL 读元数据，读完立刻释放，不占内存。
export const readKwc2026PointVideoDuration = (file: File) =>
  new Promise<number>((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const video = document.createElement('video');
    let settled = false;
    let timeoutId: number | null = null;

    const cleanup = () => {
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
        timeoutId = null;
      }
      video.removeAttribute('src');
      video.load();
      URL.revokeObjectURL(objectUrl);
    };

    const finish = (duration: number) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(duration);
    };

    const fail = () => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error('无法读取视频时长，请换一个视频文件后重试。'));
    };

    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      const duration = Number(video.duration);
      if (!Number.isFinite(duration) || duration <= 0) {
        fail();
        return;
      }
      finish(duration);
    };
    video.onerror = fail;

    timeoutId = window.setTimeout(() => {
      if (!settled) fail();
    }, 15000);

    video.src = objectUrl;
  });

export const formatKwc2026PointDuration = (totalSeconds: number) => {
  const safeSeconds = Math.max(0, Math.round(Number(totalSeconds) || 0));
  const minutes = Math.floor(safeSeconds / 60);
  const seconds = safeSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

export const formatKwc2026PointTimestamp = (timestamp: number | null | undefined) => {
  if (!Number.isFinite(Number(timestamp)) || Number(timestamp) <= 0) return '未记录';
  try {
    return new Date(Number(timestamp)).toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return '未记录';
  }
};
